import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  Edit,
  Add,
  Visibility,
  SettingsSuggest,
  ShoppingCart,
  PinDrop,
  Payments,
  CalendarToday,
} from "@mui/icons-material";
import { LoadingSpinner, ErrorMessage, CustomerTypeBadge, StatCard, AllergenChip } from "@/components/ui";
import { formatLocationLine } from "@/utils/location";
import { formatPhoneDisplay, toTelHref } from "@/constants/phoneDialCodes";
import { customerService, CustomerType } from "@/services/customer.service";
import { orderService, Order, OrderStatus } from "@/services/order.service";
import { formatCustomerName, getCustomerInitials } from "@/utils/formatCustomerName";

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING: "En attente",
  CONFIRMED: "Confirmée",
  IN_PREPARATION: "En préparation",
  READY: "Prête",
  IN_DELIVERY: "En livraison",
  DELIVERED: "Livrée",
  CANCELLED: "Annulée",
};

const ORDER_STATUS_COLORS: Record<
  OrderStatus,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  PENDING: "warning",
  CONFIRMED: "info",
  IN_PREPARATION: "info",
  READY: "primary",
  IN_DELIVERY: "primary",
  DELIVERED: "success",
  CANCELLED: "error",
};


/**
 * Page Fiche Client Détaillée
 * Inspirée de la maquette "Fiche client" : infos, préférences, activité et historique des commandes
 */
export function CustomerDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: customer,
    isLoading: customerLoading,
    error: customerError,
  } = useQuery({
    queryKey: ["customers", id],
    queryFn: () => customerService.getById(id!),
    enabled: !!id,
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ["orders", "customer", id],
    queryFn: () => orderService.getAll({ customerId: id! }),
    enabled: !!id,
  });

  const orders: Order[] = Array.isArray(ordersData) ? ordersData : (ordersData?.orders ?? []);
  const isLoading = customerLoading;
  const error = customerError;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorMessage
          message={(error as Error)?.message ?? "Client non trouvé"}
          onRetry={() => navigate("/customers")}
        />
      </Box>
    );
  }

  if (isLoading || !customer) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <LoadingSpinner message="Chargement de la fiche client..." />
      </Box>
    );
  }

  const defaultAddress = customer.addresses?.find((a) => a.isDefault) ?? customer.addresses?.[0];
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((sum, o) => sum + Number(o.totalAmount ?? 0), 0);
  const seniorityDate = customer.createdAt
    ? format(parseISO(customer.createdAt), "MMM yyyy", { locale: fr })
    : "—";
  const seniorityMonths = customer.createdAt
    ? Math.max(
        0,
        Math.floor(
          (Date.now() - parseISO(customer.createdAt).getTime()) / (30.44 * 24 * 60 * 60 * 1000)
        )
      )
    : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Fil d'Ariane */}
      <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        <Box
          component="span"
          onClick={() => navigate("/dashboard")}
          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        >
          Dashboard
        </Box>
        <span> / </span>
        <Box
          component="span"
          onClick={() => navigate("/customers")}
          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        >
          Clients
        </Box>
        <span> / </span>
        <Typography component="span" variant="body2" color="text.primary" fontWeight={600}>
          {formatCustomerName(customer)}
        </Typography>
      </Typography>

      {/* En-tête */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { md: "flex-end" },
          justifyContent: "space-between",
          gap: 2,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
            Fiche Client détaillée
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestion de la relation client personnalisée
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Chip
            icon={<Add />}
            label="Nouvelle Commande"
            onClick={() => navigate("/orders/new", { state: { customerId: customer.id } })}
            color="secondary"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            icon={<Edit />}
            label="Modifier"
            variant="outlined"
            onClick={() => navigate(`/customers/${customer.id}/edit`)}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Colonne gauche : infos + préférences */}
        <Grid item xs={12} xl={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Carte identité */}
            <Paper
              elevation={0}
              sx={{
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  height: 64,
                  bgcolor: "primary.main",
                  opacity: 0.12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
              <Box sx={{ textAlign: "center", mt: -5, pt: 4, px: 2, pb: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    mx: "auto",
                    bgcolor: "primary.main",
                    border: "4px solid",
                    borderColor: "background.paper",
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="h3" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
                    {getCustomerInitials(customer)}
                  </Typography>
                </Avatar>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  <Typography variant="h3" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                    {formatCustomerName(customer)}
                  </Typography>
                  <CustomerTypeBadge type={customer.type as CustomerType} size="small" />
                </Box>
                {customer.enterprise?.name && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {customer.enterprise.name}
                    {customer.indice != null && (
                      <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                        • Indice : {customer.indice}
                      </Typography>
                    )}
                  </Typography>
                )}
                <Box sx={{ mt: 2, textAlign: "left" }}>
                  {customer.phone && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                      <Phone sx={{ color: "text.secondary", mt: 0.25 }} fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                          Téléphone
                        </Typography>
                        <Typography variant="body2" component="div">
                          <Box
                            component="a"
                            href={toTelHref(customer.phoneDialCode, customer.phone)}
                            sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                          >
                            {formatPhoneDisplay(customer.phoneDialCode, customer.phone)}
                          </Box>
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {customer.email && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                      <Email sx={{ color: "text.secondary", mt: 0.25 }} fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                          Email
                        </Typography>
                        <Typography variant="body2" component="div">
                          <Box
                            component="a"
                            href={`mailto:${customer.email}`}
                            sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                          >
                            {customer.email}
                          </Box>
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(() => {
                    const rules = customer.enterprise?.discountRules ?? [];
                    const matchingRule =
                      customer.indice != null &&
                      rules.find(
                        (r) => customer.indice! >= r.indexMin && customer.indice! <= r.indexMax
                      );
                    if (!matchingRule) return null;
                    return (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", display: "block", mb: 1 }}>
                          Tranche de réduction appliquée
                        </Typography>
                        <Box sx={{ overflowX: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Libellé</TableCell>
                                <TableCell align="right">Indice min</TableCell>
                                <TableCell align="right">Indice max</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Réduction</TableCell>
                                <TableCell align="right">Reste à charge min</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow key={matchingRule.id}>
                                <TableCell>{matchingRule.label}</TableCell>
                                <TableCell align="right">{matchingRule.indexMin}</TableCell>
                                <TableCell align="right">{matchingRule.indexMax}</TableCell>
                                <TableCell>{matchingRule.discountType === "FIXED" ? "Fixe (€)" : "Pourcentage (%)"}</TableCell>
                                <TableCell align="right">
                                  {matchingRule.discountType === "FIXED" ? `${Number(matchingRule.discountValue).toFixed(2)} €` : `${Number(matchingRule.discountValue)} %`}
                                </TableCell>
                                <TableCell align="right">{Number(matchingRule.minChargeAmount).toFixed(2)} €</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    );
                  })()}
                  {!customer.enterprise?.discountRules?.length &&
                    customer.discountAmount != null &&
                    Number(customer.discountAmount) > 0 && (
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                        <Payments sx={{ color: "text.secondary", mt: 0.25 }} fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                            Réduction 1ère commande du jour
                          </Typography>
                          <Typography variant="body2">
                            {Number(customer.discountAmount).toFixed(2)} €
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  {customer.defaultPaymentMode && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                      <Payments sx={{ color: "text.secondary", mt: 0.25 }} fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                          Mode de paiement
                        </Typography>
                        <Typography variant="body2">
                          {customer.defaultPaymentMode === "EN_COMPTE" ? "En compte" : "Comptant"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {defaultAddress && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <LocationOn sx={{ color: "text.secondary", mt: 0.25 }} fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                          Adresse par défaut
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                          {[
                            defaultAddress.address,
                            formatLocationLine(
                              defaultAddress.postalCode,
                              defaultAddress.city,
                            ),
                          ]
                            .filter(Boolean)
                            .join("\n") || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Adresses de livraison */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  color: "primary.main",
                  fontWeight: 600,
                }}
              >
                <LocationOn fontSize="small" />
                Adresses de livraison
              </Typography>
              {customer.addresses && customer.addresses.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {customer.addresses
                    .sort((a, b) => {
                      if (a.isDefault) return -1;
                      if (b.isDefault) return 1;
                      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
                    })
                    .map((addr) => (
                      <Box
                        key={addr.id}
                        sx={{
                          p: 1.5,
                          bgcolor: "action.hover",
                          borderRadius: 1,
                          border: 1,
                          borderColor: addr.isDefault ? "primary.main" : "transparent",
                          borderLeft: addr.isDefault ? "4px solid" : undefined,
                          borderLeftColor: addr.isDefault ? "primary.main" : undefined,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          {addr.label && (
                            <Typography variant="body2" fontWeight={600}>
                              {addr.label}
                            </Typography>
                          )}
                          {addr.isDefault && (
                            <Chip label="Par défaut" size="small" color="primary" sx={{ height: 20, fontSize: "0.7rem" }} />
                          )}
                          {addr.isBilling && (
                            <Chip label="Facturation" size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                          )}
                          {addr.zone?.name && (
                            <Chip
                              label={addr.zone.name.replace(/^\d+ - /, "")}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                          {addr.address}
                          {[addr.postalCode, addr.city].filter(Boolean).length > 0
                            ? `\n${formatLocationLine(addr.postalCode, addr.city)}`
                            : ""}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune adresse de livraison.
                </Typography>
              )}
            </Paper>

            {/* Préférences de commande */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  color: "primary.main",
                  fontWeight: 600,
                }}
              >
                <SettingsSuggest fontSize="small" />
                Préférences de commande
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", display: "block", mb: 0.5 }}>
                    Composition habituelle
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {customer.takesStarter !== false && (
                      <Chip size="small" label="Entrée" variant="outlined" sx={{ fontWeight: 500 }} />
                    )}
                    {customer.takesMain !== false && (
                      <Chip size="small" label="Plat" variant="outlined" sx={{ fontWeight: 500 }} />
                    )}
                    {customer.takesDessert !== false && (
                      <Chip size="small" label="Dessert" variant="outlined" sx={{ fontWeight: 500 }} />
                    )}
                    {(customer.takesStarter === false && customer.takesMain === false && customer.takesDessert === false) && (
                      <Typography variant="body2" color="text.secondary">Non renseigné</Typography>
                    )}
                  </Box>
                </Box>
                {customer.proteinPreference && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", display: "block", mb: 0.5 }}>
                      Préférence protéines
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {customer.proteinPreference === "FISH"
                        ? "Poisson"
                        : customer.proteinPreference === "MEAT"
                          ? "Viande"
                          : customer.proteinPreference === "BOTH"
                            ? "Les deux"
                            : customer.proteinPreference === "VEGETARIAN"
                              ? "Végétarien"
                              : customer.proteinPreference}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {customer.prefersPremium === true && (
                    <Chip size="small" label="Plats premium" color="primary" variant="outlined" />
                  )}
                  {customer.prefersVegetarian === true && (
                    <Chip size="small" label="Préfère végétarien" color="success" variant="outlined" />
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Allergies et intolérances (référentiel Allergènes) */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  color: "error.main",
                  fontWeight: 600,
                }}
              >
                Allergies / Intolérances
              </Typography>
              {customer.allergens && customer.allergens.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {customer.allergens.map((ca) => (
                    <AllergenChip
                      key={ca.allergen.id}
                      label={ca.allergen.icon ? `${ca.allergen.icon} ${ca.allergen.name}` : ca.allergen.name}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune renseignée.
                </Typography>
              )}
            </Paper>

            {/* Notes internes */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  color: "primary.main",
                  fontWeight: 600,
                }}
              >
                <SettingsSuggest fontSize="small" />
                Notes internes
              </Typography>
              {customer.notes ? (
                <Typography
                  variant="body2"
                  sx={{
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  {customer.notes}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune note.
                </Typography>
              )}
            </Paper>
          </Box>
        </Grid>

        {/* Colonne droite : stats + historique */}
        <Grid item xs={12} xl={8}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* 4 cartes KPI (StatCard, aligné Dashboard) */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total commandes"
                  value={totalOrders}
                  icon={<ShoppingCart />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Chiffre d'affaires"
                  value={`${totalRevenue.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`}
                  icon={<Payments />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Zone(s) de livraison"
                  value={
                    customer.addresses?.length
                      ? [...new Set(customer.addresses.map((a) => a.zone?.name).filter(Boolean))].join(", ") || "—"
                      : "—"
                  }
                  icon={<PinDrop />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Ancienneté"
                  value={
                    seniorityMonths > 0
                      ? `${seniorityDate} (${seniorityMonths} mois)`
                      : seniorityDate
                  }
                  icon={<CalendarToday />}
                />
              </Grid>
            </Grid>

            {/* Historique des commandes */}
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: 600,
                    color: "primary.main",
                  }}
                >
                  <ShoppingCart fontSize="small" />
                  Historique des commandes
                </Typography>
                <Typography
                  component="button"
                  variant="body2"
                  fontWeight={600}
                  color="secondary.main"
                  onClick={() =>
                    navigate("/orders", {
                      state: { customerId: customer.id, customerName: formatCustomerName(customer) },
                    })
                  }
                  sx={{
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Voir tout
                </Typography>
              </Box>
              {ordersLoading ? (
                <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                  <LoadingSpinner size={32} />
                </Box>
              ) : orders.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune commande pour ce client.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem" }}>
                          N° commande
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem" }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem" }}>
                          Montant
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem" }}>
                          Statut
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders
                        .slice(0, 10)
                        .sort((a, b) => {
                          const da = a.deliveryDate ?? a.createdAt ?? "";
                          const db = b.deliveryDate ?? b.createdAt ?? "";
                          return db.localeCompare(da);
                        })
                        .map((order) => (
                          <TableRow key={order.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>
                              #{order.orderNumber ?? order.id.slice(0, 8)}
                            </TableCell>
                            <TableCell>
                              {order.deliveryDate
                                ? format(parseISO(order.deliveryDate), "dd MMM yyyy", { locale: fr })
                                : order.createdAt
                                  ? format(parseISO(order.createdAt), "dd MMM yyyy", { locale: fr })
                                  : "—"}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {Number(order.totalAmount ?? 0).toLocaleString("fr-FR", {
                                minimumFractionDigits: 2,
                              })}{" "}
                              €
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={ORDER_STATUS_LABELS[order.status]}
                                size="small"
                                color={ORDER_STATUS_COLORS[order.status]}
                                sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                aria-label="Voir la commande"
                                onClick={() => navigate(`/orders/${order.id}`)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
